import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import s from "./Collapsible.module.scss";

export type CollapsibleProps = {
  isOpen: boolean;
  style?: CSSProperties;
  baseDuration?: number;
  withDynamicDuration?: boolean;
};
export default function Collapsible({
  isOpen,
  style,
  children,
  baseDuration = 350,
  withDynamicDuration,
}: React.PropsWithChildren<CollapsibleProps>) {
  const contentRef = useRef<HTMLDivElement>(null);

  const [contentHeight, setContentHeight] = useState<number | null>(null);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const observer = new ResizeObserver(() => {
      const newHeight = element.scrollHeight;

      const computedStyle = getComputedStyle(element);
      const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

      const adjustedHeight = newHeight + marginBottom;
      setContentHeight(adjustedHeight);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const maxHeight = useMemo(
    () => (isOpen && contentHeight !== null ? `${contentHeight}px` : 0),
    [isOpen, contentHeight]
  );

  const transitionDuration = useMemo(() => {
    if (!withDynamicDuration) return baseDuration;

    // dynamically set the transition duration
    // 1. larger content should transition slower
    // 2. opening content should transition slower than closing content
    const maxDuration = baseDuration * (isOpen ? 4 : 1);
    return Math.min(
      Math.max((contentHeight ?? 0) / 2, baseDuration),
      maxDuration
    );
  }, [contentHeight, isOpen, baseDuration, withDynamicDuration]);

  return (
    <div
      className={s.Content}
      style={{
        maxHeight,
        transition: `max-height ${transitionDuration}ms ease`,
      }}
    >
      <div ref={contentRef} style={style}>
        {children}
      </div>
    </div>
  );
}
